<script setup lang="ts">
import { i18n } from "@/mixins/localization";

import AppMenuItem from "./AppMenuItem.vue";
import type { MenuItem } from "./composables/layout.ts";

const { t } = i18n.global;

const menuItems: MenuItem[] = [
  {
    label: "Hlavní menu",
    items: [
      {
        label: "Přehled",
        icon: "pi pi-fw pi-chart-line",
        to: "/admin",
      },
    ],
  },
  {
    label: "Produkty",
    items: [
      {
        label: "Hotové Produkty",
        icon: "pi pi-fw pi-box",
        to: "/admin/products",
        permission: "product:read",
      },
      {
        label: "Provozní náklady",
        icon: "pi pi-fw pi-wallet",
        to: "/admin/operational-costs",
        permission: "operational_cost:read",
      },
      {
        label: "Ingredience",
        icon: "pi pi-fw pi-chart-pie",
        to: "/admin/ingredients",
        permission: "ingredient:read",
      },
      {
        label: "Kategorie",
        icon: "pi pi-fw pi-tags",
        to: "/admin/categories",
        permission: "category:read",
      },
      {
        label: "Obaly",
        icon: "pi pi-fw pi-briefcase",
        to: "/admin/packages",
        permission: "package:read",
      },
      {
        label: "EANy",
        icon: "pi pi-fw pi-barcode",
        to: "/admin/eans",
        permission: "ean:read",
      },
      {
        label: "Nastavení teploty",
        icon: "fa-solid fa-temperature-half",
        to: "/admin/temperature-control",
        permission: "temperature_control:edit",
      },
      {
        label: "Prototypy",
        icon: "pi pi-fw pi-palette",
        to: "/admin/prototypes",
        permission: "prototype:read",
      },
      {
        label: t("views.preproducts.indexViewName"),
        icon: "pi pi-fw pi-file-edit",
        to: "/admin/preproducts",
        permission: "preproduct:read",
      },
    ],
  },
  {
    label: "Výroba",
    items: [
      {
        label: "Objednávky",
        icon: "pi pi-fw pi-inbox",
        to: "/admin/orders",
        permission: "order:read",
      },
      {
        label: "Výrobní plány",
        icon: "pi pi-fw pi-chart-bar",
        to: "/admin/production-plans",
        permission: "production_plan:read",
      },
      {
        label: "Protokoly",
        icon: "pi pi-fw pi-database",
        to: "/admin/protocols",
        permission: "protocol:read",
      },
      {
        label: "Stav skladu",
        icon: "pi pi-fw pi-list",
        to: "/admin/stock-items",
        permission: "stock_item:read",
      },
      {
        label: "Historie skladu",
        icon: "pi pi-fw pi-list",
        to: "/admin/past-stock-quantities",
        permission: "stock_item:read",
      },
      {
        label: "Mixování",
        icon: "pi pi-fw pi-cloud",
        to: "/admin/tablet-mixes",
        permission: "mixing:read",
      },
      {
        label: "Kompletace mixů",
        icon: "pi pi-fw pi-cloud",
        to: "/admin/mix-stocks",
        permission: "mix_completion:read",
      },
      {
        label: "Pick Containers",
        icon: "pi pi-fw pi-box",
        to: "/admin/pick-containers",
        permission: "pick_container:read",
      },
      {
        label: "Objednávky k picku",
        icon: "pi pi-fw pi-box",
        to: "/admin/pick-orders",
        permission: "pick_order:read",
      },
      {
        label: "Skupiny objednávek (VO)",
        icon: "pi pi-fw pi-box",
        to: "/admin/order-groups",
        permission: "order_group:read",
      },
      {
        label: "Tablety",
        icon: "pi pi-fw pi-tablet",
        to: "/admin/tablets",
        permission: "tablet:read",
      },
      {
        label: t("views.pickingSettings.indexViewName"),
        icon: "pi pi-fw pi-cog",
        to: "/admin/picking-settings",
        permission: "picking_settings:edit",
      },
    ],
  },
  {
    label: "Expedice",
    items: [
      {
        label: "Etikety",
        icon: "pi pi-print",
        to: "/admin/labels",
        permission: ["label:generate", "label:print_qr_label"],
      },
      {
        label: "Balíky",
        icon: "pi pi-box",
        to: "/admin/expedition-packages",
        permission: "expedition_package:read",
      },
      {
        label: "Info o balíku",
        icon: "pi pi-box",
        to: "/admin/expedition-user-actions",
        permission: "expedition_user_action:form",
      },
      {
        label: "Baliči",
        icon: "pi pi-users",
        to: "/admin/expedition-users",
        permission: "expedition_user:read",
      },
      {
        label: "Typy expedičních štítků",
        icon: "pi pi-tags",
        to: "/admin/expedition-label-types",
        permission: "expedition_label_type:read",
      },
      {
        label: "Kódy balíků",
        icon: "pi pi-box",
        to: "/admin/package-codes",
        permission: "package_code:read",
      },
      {
        label: "Datum předání dopravci",
        icon: "pi pi-sliders-h",
        to: "/admin/carrier-handover-date",
      },
      {
        label: t("views.deliveryEstimates.indexViewName"),
        icon: "pi pi-calendar-clock",
        to: "/admin/delivery-estimates",
        permission: "delivery_estimate:read",
      },
    ],
  },
  {
    label: "Marketing",
    items: [
      {
        label: "Statistiky",
        icon: "pi pi-fw pi-chart-bar",
        items: [
          {
            label: "Day2Day",
            icon: "pi pi-fw pi-stop",
            to: "/admin/stats/day2day",
            permission: "stats:day2day",
          },
          {
            label: "Objednávky",
            icon: "pi pi-fw pi-stop",
            to: "/admin/stats/aggregated-orders",
            permission: "stats:aggregated_orders",
          },
          {
            label: "Produkty",
            icon: "pi pi-fw pi-stop",
            to: "/admin/stats/product-sales",
            permission: "stats:product_sales",
          },
        ],
      },
    ],
  },
  {
    label: "Statistiky",
    items: [
      {
        label: "Statistika balení",
        icon: "pi pi-fw pi-chart-bar",
        to: "/admin/stats/sent-packages",
        permission: "stats:sent_packages",
      },
      {
        label: "Statistika mixování",
        icon: "pi pi-fw pi-chart-bar",
        to: "/admin/stats/mixing-mixes",
        permission: "stats:mixing_mixes",
      },
      {
        label: "Statistika kompletace mixů",
        icon: "pi pi-fw pi-chart-bar",
        to: "/admin/stats/mix-completion",
        permission: "stats:mix_completion",
      },
      {
        label: "Statistika logování",
        icon: "pi pi-fw pi-chart-bar",
        to: "/admin/stats/logging",
        permission: "stats:logging",
      },
      {
        label: "Statistika čekajících objednávek",
        icon: "pi pi-fw pi-chart-bar",
        to: "/admin/stats/waiting-orders",
        permission: "stats:waiting_orders",
      },
      {
        label: "Statistika odměn",
        icon: "pi pi-fw pi-chart-bar",
        to: "/admin/stats/rewards",
        permission: "stats:rewards",
      },
      {
        label: "Statistika individuálů dle času",
        icon: "pi pi-fw pi-chart-bar",
        to: "/admin/stats/ordered-mixes",
        permission: "stats:ordered_mixes",
      },
      {
        label: "Statistika výroby",
        icon: "pi pi-fw pi-chart-bar",
        to: "/admin/stats/production-analysis",
        permission: "stats:production_analysis",
      },
      {
        label: "Statistika pickování",
        icon: "pi pi-fw pi-chart-bar",
        to: "/admin/stats/picking",
        permission: "stats:picking_items",
      },
    ],
  },
  {
    label: "Nastavení",
    items: [
      {
        label: "Uživatelé",
        icon: "pi pi-fw pi-users",
        to: "/admin/admin-users",
        permission: "admin_user:read",
      },
      {
        label: "Role a oprávnění",
        icon: "pi pi-fw pi-user-edit",
        to: "/admin/roles",
        permission: "role:read",
      },
      {
        label: "Zaměstnanci",
        icon: "pi pi-fw pi-id-card",
        to: "/admin/employees",
        permission: "employee:read",
      },
      {
        label: "Systémové nastavení",
        icon: "pi pi-fw pi-cog",
        to: "/admin/system-settings",
        permission: "system_settings:edit",
      },
      {
        label: "Zámky jobů",
        icon: "pi pi-fw pi-unlock",
        to: "/admin/job-locks",
        permission: "job_lock:read",
      },
    ],
  },
];
</script>

<template>
  <ul class="layout-menu">
    <template v-for="(item, i) in menuItems" :key="item">
      <AppMenuItem :item="item" root :index="i" />
      <li class="menu-separator" />
    </template>
  </ul>
</template>
